import { forwardRef } from 'react'
import type { BannerLayout, BannerSizeId } from '../../lib/marketing/constants'
import { BANNER_SIZES } from '../../lib/marketing/constants'
import type { AiBannerDesign, BannerRenderData } from '../../lib/marketing/types'
import { resolveUploadImageUrl } from '../../lib/catalogUrl'

type BannerCanvasProps = {
  layout: BannerLayout
  variant?: number
  sizeId: BannerSizeId
  data: BannerRenderData
  aiDesign?: AiBannerDesign
}

function getPromoBadge(data: BannerRenderData, aiDesign?: AiBannerDesign) {
  return aiDesign?.badge || data.promoLabel
}

function ProductImage({ src, className, style }: { src?: string | null; className?: string; style?: React.CSSProperties }) {
  const url = resolveUploadImageUrl(src)
  if (!url) {
    return (
      <div className={`bg-white/20 flex items-center justify-center text-white/80 font-bold ${className}`} style={style}>
        {className?.includes('rounded-full') ? '📦' : 'Produk'}
      </div>
    )
  }
  return <img src={url} alt="" className={`object-cover ${className}`} style={style} crossOrigin="anonymous" />
}

function BrandLogo({ src, name, color, size = 64 }: { src?: string | null; name: string; color: string; size?: number }) {
  const url = resolveUploadImageUrl(src)
  if (url) {
    return <img src={url} alt={name} style={{ width: size, height: size }} className="rounded-xl object-cover border-2 border-white/30" crossOrigin="anonymous" />
  }
  return (
    <div
      style={{ width: size, height: size, backgroundColor: color }}
      className="rounded-xl flex items-center justify-center text-white font-black text-2xl border-2 border-white/30"
    >
      {name.charAt(0).toUpperCase()}
    </div>
  )
}

function PriceBlock({ data, light = false }: { data: BannerRenderData; light?: boolean }) {
  const hasDiscount = data.discountPrice && data.discountPrice !== data.price
  return (
    <div className="flex flex-col gap-1">
      {hasDiscount && (
        <span className={`text-lg line-through opacity-70 ${light ? 'text-white' : 'text-gray-500'}`}>{data.price}</span>
      )}
      <span className={`text-3xl font-black ${light ? 'text-white' : 'text-gray-900'}`}>
        {hasDiscount ? data.discountPrice : data.price}
      </span>
    </div>
  )
}

function QrBlock({ qrDataUrl, catalogUrl }: { qrDataUrl?: string; catalogUrl: string }) {
  if (!qrDataUrl) return null
  return (
    <div className="flex flex-col items-center gap-1">
      <img src={qrDataUrl} alt="QR" width={100} height={100} className="rounded-lg bg-white p-1" />
      <span className="text-[10px] text-white/80 max-w-[120px] truncate">{catalogUrl.replace(/^https?:\/\//, '')}</span>
    </div>
  )
}

export const BannerCanvas = forwardRef<HTMLDivElement, BannerCanvasProps>(function BannerCanvas(
  { layout, variant = 1, sizeId, data, aiDesign },
  ref
) {
  const size = BANNER_SIZES[sizeId]
  const isStory = size.height > size.width
  const primary = aiDesign?.primaryColor || data.brandColor
  const secondary = aiDesign?.secondaryColor || '#1e293b'
  const accent = aiDesign?.accentColor || '#f59e0b'
  const headline = aiDesign?.headline || data.productName
  const subheadline = aiDesign?.subheadline || data.brandName
  const badge = getPromoBadge(data, aiDesign)

  const baseStyle = {
    width: size.width,
    height: size.height,
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  }

  const layouts: Record<BannerLayout, React.ReactNode> = {
    'gradient-hero': (
      <div ref={ref} style={{ ...baseStyle, background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)` }} className="relative overflow-hidden flex flex-col justify-between p-10 text-white">
        <div className="flex items-center gap-4">
          <BrandLogo src={data.brandLogo} name={data.brandName} color={primary} />
          <div>
            <p className="text-sm uppercase tracking-widest opacity-80">{data.brandName}</p>
            <h1 className="text-4xl font-black leading-tight">{headline}</h1>
          </div>
        </div>
        <div className={`flex ${isStory ? 'flex-col' : 'flex-row'} items-center gap-8`}>
          <ProductImage src={data.productImage} className={`${isStory ? 'w-80 h-80' : 'w-72 h-72'} rounded-3xl shadow-2xl border-4 border-white/20`} />
          <div className="flex-1">
            <span className="inline-block px-4 py-1 rounded-full text-sm font-bold mb-3" style={{ backgroundColor: accent }}>{badge}</span>
            <PriceBlock data={data} light />
          </div>
        </div>
        <div className="flex justify-between items-end">
          <QrBlock qrDataUrl={data.qrDataUrl} catalogUrl={data.catalogUrl} />
          {data.whatsapp && <span className="text-sm">WA: {data.whatsapp}</span>}
        </div>
      </div>
    ),
    'split-left': (
      <div ref={ref} style={baseStyle} className="flex overflow-hidden bg-white">
        <div style={{ backgroundColor: primary }} className="w-1/2 p-8 flex flex-col justify-between text-white">
          <BrandLogo src={data.brandLogo} name={data.brandName} color={secondary} size={56} />
          <div>
            <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ backgroundColor: accent }}>{badge}</span>
            <h1 className="text-3xl font-black mt-4">{headline}</h1>
            <p className="opacity-80 mt-2">{subheadline}</p>
            <div className="mt-6"><PriceBlock data={data} light /></div>
          </div>
          <QrBlock qrDataUrl={data.qrDataUrl} catalogUrl={data.catalogUrl} />
        </div>
        <div className="w-1/2 flex items-center justify-center p-6 bg-gray-50">
          <ProductImage src={data.productImage} className="w-full h-full max-h-[90%] rounded-2xl shadow-xl" />
        </div>
      </div>
    ),
    'split-right': (
      <div ref={ref} style={baseStyle} className="flex overflow-hidden bg-white">
        <div className="w-1/2 flex items-center justify-center p-6 bg-gray-50">
          <ProductImage src={data.productImage} className="w-full h-full max-h-[90%] rounded-2xl shadow-xl" />
        </div>
        <div style={{ backgroundColor: secondary }} className="w-1/2 p-8 flex flex-col justify-between text-white">
          <BrandLogo src={data.brandLogo} name={data.brandName} color={primary} size={56} />
          <div>
            <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ backgroundColor: accent }}>{badge}</span>
            <h1 className="text-3xl font-black mt-4">{headline}</h1>
            <div className="mt-6"><PriceBlock data={data} light /></div>
          </div>
          <QrBlock qrDataUrl={data.qrDataUrl} catalogUrl={data.catalogUrl} />
        </div>
      </div>
    ),
    'centered-minimal': (
      <div ref={ref} style={{ ...baseStyle, backgroundColor: variant === 2 ? '#fafafa' : '#ffffff' }} className="flex flex-col items-center justify-center p-12 text-center">
        <BrandLogo src={data.brandLogo} name={data.brandName} color={primary} size={72} />
        <span className="mt-6 text-sm font-semibold px-4 py-1 rounded-full" style={{ color: primary, border: `2px solid ${primary}` }}>{badge}</span>
        <h1 className="text-4xl font-black mt-4 text-gray-900">{headline}</h1>
        <ProductImage src={data.productImage} className="w-64 h-64 rounded-full mt-8 shadow-xl border-4" style={{ borderColor: primary }} />
        <div className="mt-8"><PriceBlock data={data} /></div>
        <div className="mt-8"><QrBlock qrDataUrl={data.qrDataUrl} catalogUrl={data.catalogUrl} /></div>
      </div>
    ),
    'diagonal-stripe': (
      <div ref={ref} style={{ ...baseStyle, backgroundColor: secondary }} className="relative overflow-hidden text-white p-10">
        <div className="absolute inset-0 opacity-30" style={{ background: `linear-gradient(45deg, ${primary} 25%, transparent 25%, transparent 75%, ${primary} 75%)`, backgroundSize: '40px 40px' }} />
        <div className="relative z-10 flex flex-col h-full justify-between">
          <div className="flex justify-between items-start">
            <BrandLogo src={data.brandLogo} name={data.brandName} color={primary} />
            <span className="px-4 py-2 font-bold rounded-lg" style={{ backgroundColor: accent }}>{badge}</span>
          </div>
          <div className={`flex ${isStory ? 'flex-col' : 'flex-row'} items-center gap-8`}>
            <ProductImage src={data.productImage} className="w-64 h-64 rounded-2xl shadow-2xl" />
            <div>
              <h1 className="text-4xl font-black">{headline}</h1>
              <div className="mt-4"><PriceBlock data={data} light /></div>
            </div>
          </div>
          <QrBlock qrDataUrl={data.qrDataUrl} catalogUrl={data.catalogUrl} />
        </div>
      </div>
    ),
    'bold-frame': (
      <div ref={ref} style={{ ...baseStyle, border: `16px solid ${primary}`, backgroundColor: '#fff' }} className="p-8 flex flex-col">
        <div className="flex justify-between items-center mb-6">
          <BrandLogo src={data.brandLogo} name={data.brandName} color={primary} />
          <span className="font-bold px-4 py-2 text-white rounded-lg" style={{ backgroundColor: accent }}>{badge}</span>
        </div>
        <div className="flex-1 flex items-center gap-8">
          <ProductImage src={data.productImage} className="w-1/2 h-full max-h-[70%] rounded-xl" />
          <div>
            <h1 className="text-3xl font-black text-gray-900">{headline}</h1>
            <p className="text-gray-600 mt-2">{data.brandName}</p>
            <div className="mt-4"><PriceBlock data={data} /></div>
          </div>
        </div>
        <div className="flex justify-between items-center mt-6 pt-4 border-t">
          <QrBlock qrDataUrl={data.qrDataUrl} catalogUrl={data.catalogUrl} />
          {data.whatsapp && <span className="text-gray-600 text-sm">📱 {data.whatsapp}</span>}
        </div>
      </div>
    ),
    'circle-product': (
      <div ref={ref} style={{ ...baseStyle, background: `radial-gradient(circle at top, ${primary}33, #fff)` }} className="flex flex-col items-center justify-center p-10">
        <span className="mb-4 px-4 py-1 rounded-full text-white font-bold" style={{ backgroundColor: accent }}>{badge}</span>
        <div className="relative">
          <div className="absolute inset-0 rounded-full blur-2xl opacity-40" style={{ backgroundColor: primary }} />
          <ProductImage src={data.productImage} className="relative w-80 h-80 rounded-full border-8 border-white shadow-2xl" />
        </div>
        <h1 className="text-3xl font-black mt-8 text-gray-900 text-center">{headline}</h1>
        <p className="text-gray-600">{data.brandName}</p>
        <div className="mt-4"><PriceBlock data={data} /></div>
        <div className="mt-6"><QrBlock qrDataUrl={data.qrDataUrl} catalogUrl={data.catalogUrl} /></div>
      </div>
    ),
    'bottom-bar': (
      <div ref={ref} style={baseStyle} className="relative bg-gray-100 overflow-hidden">
        <ProductImage src={data.productImage} className="absolute inset-0 w-full h-full" />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute bottom-0 left-0 right-0 p-8 text-white" style={{ background: `linear-gradient(transparent, ${secondary}ee)` }}>
          <span className="text-xs font-bold px-3 py-1 rounded" style={{ backgroundColor: accent }}>{badge}</span>
          <h1 className="text-3xl font-black mt-2">{headline}</h1>
          <div className="flex justify-between items-end mt-4">
            <PriceBlock data={data} light />
            <QrBlock qrDataUrl={data.qrDataUrl} catalogUrl={data.catalogUrl} />
          </div>
        </div>
      </div>
    ),
    'top-badge': (
      <div ref={ref} style={{ ...baseStyle, backgroundColor: '#fff' }} className="flex flex-col">
        <div style={{ backgroundColor: primary }} className="py-4 text-center text-white font-bold text-lg">{badge}</div>
        <div className="flex-1 p-8 flex flex-col items-center justify-center">
          <BrandLogo src={data.brandLogo} name={data.brandName} color={primary} size={48} />
          <ProductImage src={data.productImage} className="w-72 h-72 rounded-2xl mt-6 shadow-lg" />
          <h1 className="text-3xl font-black mt-6 text-gray-900">{headline}</h1>
          <div className="mt-4"><PriceBlock data={data} /></div>
          <div className="mt-6"><QrBlock qrDataUrl={data.qrDataUrl} catalogUrl={data.catalogUrl} /></div>
        </div>
      </div>
    ),
    'dark-overlay': (
      <div ref={ref} style={{ ...baseStyle, backgroundColor: '#0f172a' }} className="relative text-white p-10 flex flex-col justify-between">
        <div className="absolute inset-0 opacity-20">
          <ProductImage src={data.productImage} className="w-full h-full" />
        </div>
        <div className="relative z-10 flex justify-between">
          <BrandLogo src={data.brandLogo} name={data.brandName} color={primary} />
          <span className="px-4 py-2 rounded-lg font-bold" style={{ backgroundColor: accent, color: '#000' }}>{badge}</span>
        </div>
        <div className="relative z-10">
          <h1 className="text-5xl font-black">{headline}</h1>
          <p className="text-xl opacity-80 mt-2">{data.brandName}</p>
          <div className="mt-6"><PriceBlock data={data} light /></div>
        </div>
        <div className="relative z-10 flex justify-between items-end">
          <QrBlock qrDataUrl={data.qrDataUrl} catalogUrl={data.catalogUrl} />
          {data.whatsapp && <span>WA: {data.whatsapp}</span>}
        </div>
      </div>
    ),
    'light-clean': (
      <div ref={ref} style={{ ...baseStyle, backgroundColor: variant === 3 ? '#f0fdf4' : '#f8fafc' }} className="p-10 flex flex-col">
        <div className="flex items-center gap-3 mb-8">
          <BrandLogo src={data.brandLogo} name={data.brandName} color={primary} size={48} />
          <span className="text-gray-600 font-medium">{data.brandName}</span>
        </div>
        <div className={`flex ${isStory ? 'flex-col' : 'flex-row'} gap-8 flex-1 items-center`}>
          <ProductImage src={data.productImage} className="w-64 h-64 rounded-2xl shadow-md" />
          <div>
            <span className="text-sm font-semibold" style={{ color: primary }}>{badge}</span>
            <h1 className="text-3xl font-bold text-gray-900 mt-2">{headline}</h1>
            <div className="mt-4"><PriceBlock data={data} /></div>
          </div>
        </div>
        <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
          <QrBlock qrDataUrl={data.qrDataUrl} catalogUrl={data.catalogUrl} />
        </div>
      </div>
    ),
    'festive-gold': (
      <div ref={ref} style={{ ...baseStyle, background: 'linear-gradient(180deg, #7c2d12 0%, #b45309 50%, #fbbf24 100%)' }} className="text-white p-10 flex flex-col items-center justify-center text-center">
        <span className="text-2xl mb-2">✨</span>
        <span className="px-6 py-2 rounded-full bg-white/20 font-bold text-lg">{badge}</span>
        <h1 className="text-4xl font-black mt-6">{headline}</h1>
        <ProductImage src={data.productImage} className="w-72 h-72 rounded-2xl mt-8 border-4 border-yellow-200 shadow-2xl" />
        <div className="mt-6"><PriceBlock data={data} light /></div>
        <p className="mt-4 opacity-90">{data.brandName}</p>
        <div className="mt-6"><QrBlock qrDataUrl={data.qrDataUrl} catalogUrl={data.catalogUrl} /></div>
      </div>
    ),
    'sale-strike': (
      <div ref={ref} style={{ ...baseStyle, backgroundColor: '#fef2f2' }} className="p-10 flex flex-col">
        <div className="flex justify-between items-start">
          <BrandLogo src={data.brandLogo} name={data.brandName} color="#dc2626" />
          <span className="bg-red-600 text-white px-6 py-3 rounded-xl font-black text-xl rotate-3 shadow-lg">{badge}</span>
        </div>
        <div className="flex-1 flex items-center gap-8 mt-8">
          <ProductImage src={data.productImage} className="w-64 h-64 rounded-2xl" />
          <div>
            <h1 className="text-3xl font-black text-gray-900">{headline}</h1>
            <div className="mt-4"><PriceBlock data={data} /></div>
          </div>
        </div>
        <div className="flex justify-between items-center mt-6">
          <QrBlock qrDataUrl={data.qrDataUrl} catalogUrl={data.catalogUrl} />
        </div>
      </div>
    ),
    'grid-accent': (
      <div ref={ref} style={baseStyle} className="grid grid-cols-2 grid-rows-2 overflow-hidden">
        <div style={{ backgroundColor: primary }} className="p-6 text-white flex flex-col justify-center">
          <BrandLogo src={data.brandLogo} name={data.brandName} color={secondary} size={40} />
          <h1 className="text-2xl font-black mt-4">{headline}</h1>
          <span className="mt-2 text-sm font-bold" style={{ color: accent }}>{badge}</span>
        </div>
        <ProductImage src={data.productImage} className="w-full h-full" />
        <div className="bg-white p-6 flex items-center"><PriceBlock data={data} /></div>
        <div style={{ backgroundColor: secondary }} className="p-6 flex items-center justify-center">
          <QrBlock qrDataUrl={data.qrDataUrl} catalogUrl={data.catalogUrl} />
        </div>
      </div>
    ),
    'wave-bottom': (
      <div ref={ref} style={{ ...baseStyle, backgroundColor: '#e0f2fe' }} className="relative overflow-hidden p-10 flex flex-col">
        <div className="flex items-center gap-4">
          <BrandLogo src={data.brandLogo} name={data.brandName} color={primary} />
          <span className="font-bold px-3 py-1 rounded-full text-white" style={{ backgroundColor: primary }}>{badge}</span>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <ProductImage src={data.productImage} className="w-80 h-80 rounded-3xl shadow-xl" />
        </div>
        <div className="text-center">
          <h1 className="text-3xl font-black text-gray-900">{headline}</h1>
          <div className="mt-2 flex justify-center"><PriceBlock data={data} /></div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-24" style={{ background: `url("data:image/svg+xml,%3Csvg viewBox='0 0 1200 120' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0,60 C300,120 900,0 1200,60 L1200,120 L0,120 Z' fill='${encodeURIComponent(primary)}'/%3E%3C/svg%3E") bottom/cover no-repeat` }} />
        <div className="relative z-10 flex justify-center mt-4">
          <QrBlock qrDataUrl={data.qrDataUrl} catalogUrl={data.catalogUrl} />
        </div>
      </div>
    ),
  }

  return (
    <div className="fixed -left-[99999px] top-0 pointer-events-none" aria-hidden>
      {layouts[layout]}
    </div>
  )
})
